/**
 * W23-4 — Restore New Report dialog draft after refresh.
 *
 * The "A NEWER VERSION OF DATACRATE IS AVAILABLE — REFRESH TO CONTINUE."
 * toast appears mid-flow when a new build deploys. Clicking Refresh tears
 * the wizard down before the user has clicked Generate, so all in-progress
 * dialog state (step / report type / dirty edits) vaporises.
 *
 * Fix: snapshot a lightweight summary of the wizard to `sessionStorage`
 * on `beforeunload`, then read it back on mount and offer a one-click
 * restore via a non-blocking banner.
 *
 * Storage choice — `sessionStorage`, NOT `localStorage`:
 *   1. The intent is to survive a single tab-level refresh, not a tab
 *      close. Drafts older than the tab itself are user-intentional
 *      throwaways.
 *   2. The wizard already collects a *separate* concept of "site draft"
 *      (see src/services/wizardDraft.ts) keyed in localStorage. That
 *      survives session boundaries by design. This helper is a strictly
 *      smaller, more ephemeral artifact — keeping them in different
 *      storage backends makes the contract obvious at a glance.
 *
 * Age guard: read-side enforces `MAX_AGE_MS = 30 min`. Older drafts are
 * returned as `null` and silently swept from storage so we don't carry
 * stale state across long-lived tabs (eg. a user who left the wizard
 * open overnight).
 *
 * Key shape: `reportDialogDraft.{siteId}.{reportType}`. The `(siteId,
 * reportType)` tuple isolates draft restoration so picking a different
 * report type for the same site doesn't accidentally restore stale
 * intent for the previous type.
 */

export type ReportDialogDraftStep =
  | 'type'
  | 'disambiguation'
  | 'preferences'
  | 'generating';

/**
 * Lightweight snapshot of the New Report wizard. Only the fields that
 * are cheap to serialise + cheap to re-apply are persisted:
 *
 *   - `step` — restores the user to the same panel they were on.
 *   - `reportType` / `mode` — survive the type-select choice + the
 *     disambiguation pick.
 *   - `existingReportId` — lets the restorer re-link to the parent
 *     report (we look it up against `siteRag.reports` on mount; we do
 *     NOT persist the whole report object).
 *   - `dirtyFields` — preserves the dirty-tracking categories so the
 *     cancel-confirm flow still pluralises correctly post-restore.
 *
 * `intentPatch` is reserved for any per-dialog ProjectIntent overrides
 * the user typed but hasn't yet committed to `site.projectIntent`. The
 * current wizard commits intent edits to the site immediately via
 * `updateSite()`, so this field is empty in practice — it's exposed
 * here so a future "stash unsaved intent" enhancement doesn't require
 * a schema bump.
 */
export interface ReportDialogDraft {
  /** When the snapshot was written (ms since epoch). */
  savedAt: number;
  /** Site this draft belongs to — part of the storage key. */
  siteId: string;
  /** Report type this draft belongs to — part of the storage key. */
  reportType: string;
  /** Which wizard panel was active. */
  step: ReportDialogDraftStep;
  /** Update / replace / add-another / new (mirrors WizardMode). */
  mode?: 'new' | 'update' | 'replace' | 'add-another';
  /** Parent report id (for update / replace). */
  existingReportId?: string;
  /** Dirty-tracking categories the user touched. */
  dirtyFields?: ReadonlyArray<'preferences' | 'documents' | 'archetype'>;
  /** Reserved for future intent-stash use. */
  intentPatch?: Record<string, unknown>;
  /** Client run id when step === 'generating' (matches activeGenerations key). */
  runId?: string;
}

/** 30 minutes — drafts older than this are swept on read. */
export const MAX_AGE_MS = 30 * 60 * 1000;

const KEY_PREFIX = 'reportDialogDraft';

/** Storage key for a given (siteId, reportType) tuple. */
export function draftKey(siteId: string, reportType: string): string {
  return `${KEY_PREFIX}.${siteId}.${reportType}`;
}

/**
 * Resolve the sessionStorage instance to read/write against. In SSR or
 * jsdom-less test runs `window` is undefined; callers should swallow
 * the `null` return and skip persistence rather than crash.
 */
function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Write a draft snapshot to sessionStorage. No-op if storage is
 * unavailable or the JSON.stringify throws (eg. quota exceeded).
 */
export function saveReportDialogDraft(draft: ReportDialogDraft): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(draftKey(draft.siteId, draft.reportType), JSON.stringify(draft));
  } catch {
    // Quota exceeded or storage disabled — silently drop the snapshot.
    // The user keeps their in-memory state; the only thing they lose
    // is the post-refresh restore, which is already a best-effort
    // affordance.
  }
}

/**
 * Read a draft snapshot for the given (siteId, reportType) tuple.
 *
 * Returns `null` when:
 *   - storage is unavailable
 *   - no draft exists
 *   - the stored JSON is corrupt
 *   - the draft is older than `MAX_AGE_MS`
 *
 * Stale + corrupt entries are swept from storage as a side effect so
 * subsequent reads don't keep paying the parse cost.
 */
export function loadReportDialogDraft(
  siteId: string,
  reportType: string,
  now: number = Date.now(),
): ReportDialogDraft | null {
  const storage = getStorage();
  if (!storage) return null;
  const key = draftKey(siteId, reportType);
  const raw = storage.getItem(key);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    try { storage.removeItem(key); } catch { /* ignore */ }
    return null;
  }
  if (!isReportDialogDraft(parsed)) {
    try { storage.removeItem(key); } catch { /* ignore */ }
    return null;
  }
  if (now - parsed.savedAt > MAX_AGE_MS) {
    try { storage.removeItem(key); } catch { /* ignore */ }
    return null;
  }
  // Ensure the tuple in storage matches the requested tuple. This
  // guards against the (extremely unlikely) case where two keys collide
  // after a refactor that changes KEY_PREFIX semantics.
  if (parsed.siteId !== siteId || parsed.reportType !== reportType) {
    return null;
  }
  return parsed;
}

/** Persist an in-flight generating snapshot (wizard background-close or store upsert). */
export function saveGeneratingDraft(args: {
  siteId: string;
  reportType: string;
  runId: string;
  mode?: ReportDialogDraft['mode'];
  existingReportId?: string;
  dirtyFields?: ReadonlyArray<'preferences' | 'documents' | 'archetype'>;
}): void {
  saveReportDialogDraft({
    savedAt: Date.now(),
    siteId: args.siteId,
    reportType: args.reportType,
    step: 'generating',
    runId: args.runId,
    ...(args.mode ? { mode: args.mode } : {}),
    ...(args.existingReportId ? { existingReportId: args.existingReportId } : {}),
    ...(args.dirtyFields && args.dirtyFields.length > 0 ? { dirtyFields: args.dirtyFields } : {}),
  });
}

/**
 * Scan sessionStorage for every non-stale draft with step === 'generating'.
 * Used by sidebar + site banners after reload when activeGenerations is empty.
 */
export function findAllGeneratingDrafts(
  now: number = Date.now(),
): ReportDialogDraft[] {
  const storage = getStorage();
  if (!storage) return [];
  const drafts: ReportDialogDraft[] = [];
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith(`${KEY_PREFIX}.`)) keys.push(k);
  }
  for (const key of keys) {
    const raw = storage.getItem(key);
    if (!raw) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      continue;
    }
    if (!isReportDialogDraft(parsed) || parsed.step !== 'generating') continue;
    if (now - parsed.savedAt > MAX_AGE_MS) continue;
    drafts.push(parsed);
  }
  return drafts.sort((a, b) => b.savedAt - a.savedAt);
}

/** Remove a draft (called on Generate success, Discard, or 30-min sweep). */
export function clearReportDialogDraft(siteId: string, reportType: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(draftKey(siteId, reportType));
  } catch {
    /* ignore */
  }
}

/**
 * Scan sessionStorage for every non-stale draft belonging to `siteId`,
 * newest first. Used by the wizard on mount so it can surface a
 * restore banner WITHOUT the caller having to know which reportType
 * the user was working on. Sweeps stale + corrupt entries as a side
 * effect. Returns `[]` when storage is unavailable.
 */
export function findReportDialogDraftsForSite(
  siteId: string,
  now: number = Date.now(),
): ReportDialogDraft[] {
  const storage = getStorage();
  if (!storage) return [];
  const prefix = `${KEY_PREFIX}.${siteId}.`;
  const drafts: ReportDialogDraft[] = [];
  const toSweep: string[] = [];
  // Snapshot keys up-front — removing during iteration shifts indices.
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith(prefix)) keys.push(k);
  }
  for (const key of keys) {
    const raw = storage.getItem(key);
    if (!raw) continue;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      toSweep.push(key);
      continue;
    }
    if (!isReportDialogDraft(parsed)) {
      toSweep.push(key);
      continue;
    }
    if (parsed.siteId !== siteId) continue;
    if (now - parsed.savedAt > MAX_AGE_MS) {
      toSweep.push(key);
      continue;
    }
    drafts.push(parsed);
  }
  for (const k of toSweep) {
    try { storage.removeItem(k); } catch { /* ignore */ }
  }
  return drafts.sort((a, b) => b.savedAt - a.savedAt);
}

/**
 * Returns `true` when the draft contains user progress worth restoring.
 * A draft that only carries `step === 'type'` with no other fields is
 * effectively a fresh wizard — restoring it is noise.
 */
export function isNonTrivialDraft(draft: ReportDialogDraft): boolean {
  if (draft.step === 'generating') return true;
  if (draft.step !== 'type') return true;
  if (draft.mode && draft.mode !== 'new') return true;
  if (draft.existingReportId) return true;
  if (draft.dirtyFields && draft.dirtyFields.length > 0) return true;
  if (draft.intentPatch && Object.keys(draft.intentPatch).length > 0) return true;
  return false;
}

/** Type guard — mirrors the public `ReportDialogDraft` shape. */
function isReportDialogDraft(value: unknown): value is ReportDialogDraft {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.savedAt !== 'number' || !Number.isFinite(v.savedAt)) return false;
  if (typeof v.siteId !== 'string' || v.siteId === '') return false;
  if (typeof v.reportType !== 'string' || v.reportType === '') return false;
  if (typeof v.step !== 'string') return false;
  if (
    v.step !== 'type'
    && v.step !== 'disambiguation'
    && v.step !== 'preferences'
    && v.step !== 'generating'
  ) return false;
  if (v.runId !== undefined && typeof v.runId !== 'string') return false;
  if (v.mode !== undefined) {
    if (v.mode !== 'new' && v.mode !== 'update' && v.mode !== 'replace' && v.mode !== 'add-another') {
      return false;
    }
  }
  if (v.existingReportId !== undefined && typeof v.existingReportId !== 'string') return false;
  if (v.dirtyFields !== undefined) {
    if (!Array.isArray(v.dirtyFields)) return false;
    for (const f of v.dirtyFields) {
      if (f !== 'preferences' && f !== 'documents' && f !== 'archetype') return false;
    }
  }
  if (v.intentPatch !== undefined) {
    if (!v.intentPatch || typeof v.intentPatch !== 'object' || Array.isArray(v.intentPatch)) return false;
  }
  return true;
}
